import type { HeaterType, RoofKind } from "../../engines/types";
import type { getT } from "../../i18n";

export interface ToolGroup {
	label: string;
	items: string[];
}

interface BuildToolOptions {
	substepId?: string;
	roofKind?: RoofKind;
}

function normalizeTools(groups: ToolGroup[], commonLabel: string): ToolGroup[] {
	return [
		{
			label: commonLabel,
			items: groups.flatMap((group) => group.items),
		},
	];
}

function getToolLibrary(
	t: ReturnType<typeof getT>,
): Record<string, ToolGroup[]> {
	return {
		foundation: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [t.tool_measuringStick, t.tool_chalkLine, t.tool_screwdriver],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [t.tool_earthAugerOrSpade, t.tool_laserLevel, t.tool_mitreSaw],
			},
		],
		deck: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [t.tool_screwdriver, t.tool_mitreSaw, t.tool_measuringStick],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [t.tool_spiritLevel, t.tool_spacers5mm],
			},
		],
		framing: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [t.tool_spiritLevel, t.tool_clamps, t.tool_hammer],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [t.tool_mitreSaw, t.tool_nailGunOrScrewdriver],
			},
		],
		roof: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [t.tool_ladder, t.tool_utilityKnife, t.tool_hammer],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [t.tool_nailGun, t.tool_fallProtection],
			},
		],
		facade: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [t.tool_carpenterSquare, t.tool_nailPunch],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [t.tool_panelSaw, t.tool_scaffoldOrTrestles],
			},
		],
		// Facade treatment (issue 134). Its own group rather than extra items on
		// `facade`: someone building an untreated facade should not be told to
		// buy brushes, and the substep that uses this only exists when a colour
		// was chosen.
		facadeTreatment: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [
					t.tool_facadeBrush,
					t.tool_paintBucketAndStirrer,
					t.tool_dropCloth,
				],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [t.tool_scaffoldOrTrestles, t.tool_protectiveGloves],
			},
		],
		insulation: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [t.tool_insulationKnife, t.tool_protectiveGloves],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [t.tool_panelClipTool],
			},
		],
		benches: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [t.tool_plungeSaw, t.tool_sandingBlock, t.tool_screwdriver],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [t.tool_routerRoundedEdges],
			},
		],
		systems: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [t.tool_tapeMeasure, t.tool_drillPress, t.tool_screwdriver],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [t.tool_multimeter, t.tool_torqueScrewdriver, t.tool_tinSnips],
			},
		],
		drainage: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [t.tool_spade, t.tool_straightedge, t.tool_spiritLevel],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [t.tool_laserLevel, t.tool_groundCompactor, t.tool_pipeCutter],
			},
		],
		chimney: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [t.tool_tapeMeasure, t.tool_screwdriver, t.tool_rivetTool],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [
					t.tool_chimneyPipeCutter,
					t.tool_tinSnips,
					t.tool_scaffoldOrFallProtection,
				],
			},
		],
		handover: [
			{
				label: t.domain_build_toolGroupCommon,
				items: [t.tool_checklist, t.tool_mobileCamera],
			},
			{
				label: t.domain_build_toolGroupSpecialist,
				items: [t.tool_moistureMeterOptional],
			},
		],
	};
}

export function getTools(
	stepId: string,
	heaterType: HeaterType,
	includesShower: boolean,
	t: ReturnType<typeof getT>,
	options: BuildToolOptions = {},
) {
	const TOOL_LIBRARY = getToolLibrary(t);
	const commonLabel = t.domain_build_toolGroupCommon;
	const { substepId, roofKind } = options;

	if (substepId === "framing--substep-01") {
		return normalizeTools(
			[
				{
					label: commonLabel,
					items: [t.tool_measuringStick, t.tool_spiritLevel, t.tool_clamps],
				},
				{
					label: t.domain_build_toolGroupSpecialist,
					items: [t.tool_hammer, t.tool_nailGunOrScrewdriver],
				},
			],
			commonLabel,
		);
	}

	if (substepId === "framing--substep-02") {
		return normalizeTools(
			[
				{
					label: commonLabel,
					items: [t.tool_carpenterSquare, t.tool_screwdriver],
				},
				{
					label: t.domain_build_toolGroupSpecialist,
					items: [t.tool_clamps, t.tool_glassSuctionLifter],
				},
			],
			commonLabel,
		);
	}

	if (substepId === "roof--substep-01") {
		return normalizeTools(
			[
				{
					label: commonLabel,
					items: [t.tool_ladder, t.tool_hammer, t.tool_spiritLevel],
				},
				{
					label: t.domain_build_toolGroupSpecialist,
					items: [t.tool_nailGun, t.tool_fallProtection],
				},
			],
			commonLabel,
		);
	}

	if (substepId === "roof--substep-03" && roofKind === "barrel") {
		return normalizeTools(
			[
				{
					label: commonLabel,
					items: [
						t.tool_rubberMallet,
						t.tool_bandTensioner,
						t.tool_measuringStick,
					],
				},
				{
					label: t.domain_build_toolGroupSpecialist,
					items: [t.tool_ladder, t.tool_fallProtection],
				},
			],
			commonLabel,
		);
	}

	if (substepId === "insulation--substep-02") {
		return normalizeTools(
			[
				{
					label: commonLabel,
					items: [t.tool_utilityKnife, t.tool_stapleGun, t.tool_aluminiumTape],
				},
				{
					label: t.domain_build_toolGroupSpecialist,
					items: [t.tool_protectiveGloves],
				},
			],
			commonLabel,
		);
	}

	if (substepId === "insulation--substep-03") {
		return normalizeTools(
			[
				{
					label: commonLabel,
					items: [t.tool_panelClipTool, t.tool_screwdriver, t.tool_spacers5mm],
				},
				{
					label: t.domain_build_toolGroupSpecialist,
					items: [t.tool_sandingBlock],
				},
			],
			commonLabel,
		);
	}

	if (substepId === "benches--substep-01") {
		return normalizeTools(
			[
				{
					label: commonLabel,
					items: [t.tool_plungeSaw, t.tool_sandingBlock, t.tool_screwdriver],
				},
				{
					label: t.domain_build_toolGroupSpecialist,
					items: [t.tool_routerRoundedEdges],
				},
			],
			commonLabel,
		);
	}

	if (substepId === "systems--substep-01") {
		return heaterType === "wood"
			? normalizeTools(
					[
						{
							label: commonLabel,
							items: [
								t.tool_fireboardCutter,
								t.tool_tapeMeasure,
								t.tool_screwdriver,
							],
						},
						{
							label: t.domain_build_toolGroupSpecialist,
							items: [t.tool_tinSnips, t.tool_torqueScrewdriver],
						},
					],
					commonLabel,
				)
			: normalizeTools(
					[
						{
							label: commonLabel,
							items: [
								t.tool_tapeMeasure,
								t.tool_screwdriver,
								t.tool_multimeter,
							],
						},
						{
							label: t.domain_build_toolGroupSpecialist,
							items: [t.tool_torqueScrewdriver],
						},
					],
					commonLabel,
				);
	}

	if (substepId === "systems--substep-02") {
		return normalizeTools(
			[
				{
					label: commonLabel,
					items: [t.tool_tapeMeasure, t.tool_ventDrillJig, t.tool_screwdriver],
				},
				{ label: t.domain_build_toolGroupSpecialist, items: [t.tool_tinSnips] },
			],
			commonLabel,
		);
	}

	if (substepId === "chimney--substep-01") {
		return normalizeTools(
			[
				{
					label: commonLabel,
					items: [t.tool_tapeMeasure, t.tool_rivetTool, t.tool_screwdriver],
				},
				{
					label: t.domain_build_toolGroupSpecialist,
					items: [
						t.tool_chimneyPipeCutter,
						t.tool_tinSnips,
						t.tool_scaffoldOrFallProtection,
					],
				},
			],
			commonLabel,
		);
	}

	if (stepId === "systems" && heaterType === "wood") {
		return normalizeTools(
			[
				TOOL_LIBRARY.systems[0],
				{
					label: t.domain_build_toolGroupSpecialist,
					items: [
						t.tool_fireboardCutter,
						t.tool_ventDrillJig,
						t.tool_torqueScrewdriver,
					],
				},
			],
			commonLabel,
		);
	}

	if (stepId === "drainage") {
		return includesShower
			? normalizeTools(TOOL_LIBRARY.drainage, commonLabel)
			: normalizeTools(
					[
						{
							label: t.domain_build_toolGroupCommon,
							items: [t.tool_spade, t.tool_wheelbarrow, t.tool_spiritLevel],
						},
						{
							label: t.domain_build_toolGroupSpecialist,
							items: [t.tool_groundFabric, t.tool_groundCompactor],
						},
					],
					commonLabel,
				);
	}

	return normalizeTools(
		TOOL_LIBRARY[stepId] ?? TOOL_LIBRARY.handover,
		commonLabel,
	);
}

export function getBadge(
	stepId: string,
	heaterType: HeaterType,
	includesShower: boolean,
	t: ReturnType<typeof getT>,
) {
	if (stepId === "systems") {
		return heaterType === "electric"
			? {
					professionalBadge: t.domain_build_badgeElectrician,
					professionalReason: t.domain_build_badgeElectricianReason,
				}
			: {
					professionalBadge: t.domain_build_badgeDiy,
					professionalReason: t.domain_build_badgeSystemsDiyReason,
				};
	}

	if (stepId === "chimney") {
		return {
			professionalBadge: t.domain_build_badgeChimneySweep,
			professionalReason: t.domain_build_badgeChimneyReason,
		};
	}

	if (stepId === "drainage" && includesShower) {
		return {
			professionalBadge: t.domain_build_badgePlumber,
			professionalReason: t.domain_build_badgePlumberReason,
		};
	}

	return {
		professionalBadge: t.domain_build_badgeDiy,
		professionalReason: t.domain_build_badgeDiyReason,
	};
}
